import React, { useState, useEffect, useRef } from 'react';

import {

  Box, Grid, Alert, CircularProgress, Divider, FormControlLabel, Radio, RadioGroup, Typography,

} from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { useHistory, useRouteMatch, useParams } from 'react-router-dom';

import { useForm, Controller } from 'react-hook-form';

import { toast } from 'react-toastify';

import axios from '../../../login/Axios-interceptor';

import { getInventoryHeaders, buildInventoryUrl } from '../../api/inventory-request';

import PageHeader from '../../components/PageHeader';

import InvButton from '../../components/InvButton';

import { InvTextField, InvSelect } from '../../components/InvField';

import VendorDocumentsPanel from '../../components/material/VendorDocumentsPanel';

import {
  createVendor, getVendor, updateVendor,
  fromApiVendor, toVendorApiPayload, isVendorActive,
} from '../../api/vendor';

import { VENDOR_TYPES, VENDOR_CURRENCIES } from '../../constants';

import { emptyVendorForm, PHARMA_VENDOR_TYPES } from '../../utils/vendorEntity';

import { getVendorFormMode, isFieldEditable } from '../../utils/vendorWorkflow';

import { IPD_COLORS, cardSx } from '../../theme';



const FORM_ID = 'vendor-form';

const VENDOR_FIELD_ORDER = [
  'vendor_code',
  'vendor_name',
  'vendor_type',
  'address',
  'state',
  'city',
  'country',
  'pincode',
  'contact_person',
  'phone',
  'email',
  'gstin',
  'pan',
  'drug_license_no',
  'license_valid_till',
  'payment_terms',
  'credit_period',
  'currency',
];

/** Expand any collapsed Accordion / <details> wrapping the field before focus. */
function expandCollapsedAncestors(el) {
  let node = el;
  while (node && node !== document.body) {
    if (node.tagName === 'DETAILS' && !node.open) {
      node.open = true;
    }
    if (node.classList?.contains('MuiAccordion-root')) {
      const closed = node.querySelector('[aria-expanded="false"]');
      if (closed) closed.click();
    }
    if (node.classList?.contains('MuiCollapse-hidden')) {
      const accordion = node.closest('.MuiAccordion-root');
      const closed = accordion?.querySelector('[aria-expanded="false"]');
      if (closed) closed.click();
    }
    node = node.parentElement;
  }
}

function focusVendorField(field) {
  const anchor = document.getElementById(`vendor-field-${field}`);
  if (!anchor) return;

  expandCollapsedAncestors(anchor);

  // Keep the control fully in view; sticky headers / helper text need center bias.
  anchor.style.scrollMarginTop = '96px';
  anchor.style.scrollMarginBottom = '48px';
  anchor.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

  const focusable = anchor.querySelector(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [role="combobox"], .MuiSelect-select, [tabindex]:not([tabindex="-1"])',
  );
  if (!focusable) return;

  // Wait for smooth scroll + any accordion expand animation before focusing.
  window.setTimeout(() => {
    try {
      focusable.focus({ preventScroll: true });
    } catch (_) {
      focusable.focus();
    }
  }, 280);
}

function SectionTitle({ label, first = false }) {

  return (

    <Grid item xs={12}>

      <Box sx={{ mt: first ? 0 : 1, mb: 0.5 }}>

        <Typography sx={{ fontSize: 15, fontWeight: 600, color: IPD_COLORS.textPrimary, mb: 1 }}>

          {label}

        </Typography>

        <Divider sx={{ borderColor: IPD_COLORS.border }} />

      </Box>

    </Grid>

  );

}



export default function VendorForm() {

  const { id } = useParams();

  const { url } = useRouteMatch();

  const history = useHistory();

  const isEdit = Boolean(id);

  const listUrl = url.replace(/\/new$/, '').replace(/\/[^/]+\/edit$/, '');



  const [saving, setSaving] = useState(false);

  const [initialLoading, setInitialLoading] = useState(isEdit);

  const [loadedValues, setLoadedValues] = useState(emptyVendorForm());

  const [existingDocuments, setExistingDocuments] = useState({});

  const [saveError, setSaveError] = useState(null);

  const [approvalStatus, setApprovalStatus] = useState(null);

  const formMode = getVendorFormMode(approvalStatus);

  const readOnly = formMode === 'readonly';



  const {

    control,

    handleSubmit,

    reset,

    watch,

    setValue,

    setFocus,

    clearErrors,

    formState: { errors },

  } = useForm({
    defaultValues: emptyVendorForm(),
    mode: 'onSubmit',
    // After first Save, clear / update field errors as the user types or picks values.
    reValidateMode: 'onChange',
    // Custom onInvalid handles scroll + focus (including Select / date).
    shouldFocusError: false,
  });



  const isActive = watch('is_active');

  const vendorType = watch('vendor_type');

  const watchedPincode = watch('pincode');

  const isPharma = PHARMA_VENDOR_TYPES.has(vendorType);

  // Pharma-only required fields — drop stale errors when type changes away from pharma.
  useEffect(() => {
    if (!isPharma) {
      clearErrors(['drug_license_no', 'license_valid_till']);
    }
  }, [isPharma, clearErrors]);

  const [statesList, setStatesList] = useState([]);
  const [citiesList, setCitiesList] = useState([]);
  const pendingDistrictRef = useRef(null);

  useEffect(() => {
    axios.get(buildInventoryUrl('get_all_states'), { headers: getInventoryHeaders() })
      .then(res => setStatesList(res.data.state_list || []))
      .catch(() => {});
  }, []);

  function fetchCities(stateName, pendingDistrict) {
    if (!stateName) { setCitiesList([]); return; }
    if (pendingDistrict !== undefined) pendingDistrictRef.current = pendingDistrict;
    axios.post(
      buildInventoryUrl('get_filtered_districts'),
      JSON.stringify({ state_name: stateName }),
      { headers: getInventoryHeaders(true) }
    ).then(res => {
      const list = res.data.district_list || [];
      setCitiesList(list);
      const pending = pendingDistrictRef.current;
      if (pending) {
        const match = list.find(d => d.district_name.toLowerCase() === pending.toLowerCase());
        if (match) setValue('city', match.district_name);
        pendingDistrictRef.current = null;
      }
    }).catch(() => {});
  }

  useEffect(() => {
    if (watchedPincode && /^\d{6}$/.test(watchedPincode)) {
      handlePincodeBlur(watchedPincode);
    }
  }, [watchedPincode]); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePincodeBlur(pincode) {
    if (!pincode || String(pincode).length < 6) return;
    axios.get(
      buildInventoryUrl(`get_pincode_details?pincode=${pincode}`),
      { headers: getInventoryHeaders() }
    ).then(res => {
      const details = res.data.pincode_details;
      if (!details || details.length === 0) return;
      const first = details[0];
      const matchedState = statesList.find(
        s => s.state_name.toLowerCase() === first.statename.toLowerCase()
      );
      const stateName = matchedState ? matchedState.state_name : first.statename;
      setValue('state', stateName);
      setValue('city', '');
      fetchCities(stateName, first.district);
    }).catch(() => {});
  }

  function fieldDisabled(name) {

    return readOnly || !isFieldEditable(name, formMode) || saving;

  }



  useEffect(() => {

    if (!isEdit) return;

    let cancelled = false;

    (async () => {

      setInitialLoading(true);

      try {

        const vendor = await getVendor(id);

        const values = fromApiVendor(vendor);

        if (!cancelled) {

          setLoadedValues(values);

          setExistingDocuments(vendor.documents || {});

          setApprovalStatus(vendor.approval_status || null);

          reset(values);

          if (values.state) fetchCities(values.state);

        }

      } catch (e) {

        if (!cancelled) {

          toast.error(e.message || 'Failed to load vendor');

          history.replace(listUrl);

        }

      } finally {

        if (!cancelled) setInitialLoading(false);

      }

    })();

    return () => { cancelled = true; };

  }, [id, isEdit, reset, history, listUrl]);



  async function onSubmit(data) {
    setSaving(true);
    setSaveError(null);
    try {
      const body = toVendorApiPayload(data, { isEdit, existingDocuments });
      let vendorId = id;

      if (isEdit) {
        await updateVendor(id, body);
        toast.success('Vendor updated successfully');
      } else {
        const created = await createVendor(body);
        vendorId = created?.id || created?.vendor_id;
        toast.success('Vendor saved as draft');
      }

      history.push(vendorId ? `${listUrl}/${vendorId}${!isEdit ? '?saved=1' : ''}` : listUrl);
    } catch (e) {
      setSaveError(e.message || 'Failed to save vendor');
    } finally {
      setSaving(false);
    }
  }

  function onInvalid(formErrors) {
    const firstErrorField = VENDOR_FIELD_ORDER.find((name) => formErrors[name])
      || Object.keys(formErrors).find((name) => name !== 'documentRows')
      || Object.keys(formErrors)[0];
    if (!firstErrorField) return;
    // Prefer DOM scroll+focus so Select / date pickers work; RHF setFocus is a best-effort extra.
    focusVendorField(firstErrorField);
    try {
      setFocus(firstErrorField);
    } catch (_) { /* InvSelect / date may not expose a focusable RHF ref */ }
  }



  const typeOptions = VENDOR_TYPES.map(t => ({ value: t.value, label: t.label }));

  const currencyOptions = VENDOR_CURRENCIES.map(c => ({ value: c.value, label: c.label }));



  if (initialLoading) {

    return (

      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>

        <CircularProgress sx={{ color: IPD_COLORS.primary }} />

      </Box>

    );

  }



  return (

    <Box sx={{ color: IPD_COLORS.textPrimary, pb: 4 }}>

      <PageHeader

        eyebrow="Material Management"

        title={isEdit ? 'Edit Vendor' : 'New Vendor'}

        description={isEdit
          ? 'Update vendor profile, compliance, and supporting documents.'
          : 'Complete vendor details and save. Submit for approval from the vendor profile when ready.'}

        actions={

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>

            <InvButton variant="outlined" tone="teal" startIcon={<ArrowBackIcon />}

              onClick={() => history.push(listUrl)}>

              Back

            </InvButton>

            {!readOnly && (

              <InvButton variant="outlined" tone="teal"

                onClick={() => reset(isEdit ? loadedValues : emptyVendorForm())}>

                Reset

              </InvButton>

            )}

            {!readOnly && (
              <InvButton type="submit" form={FORM_ID} loading={saving}>
                {isEdit ? 'Save Changes' : 'Save Vendor'}
              </InvButton>
            )}

          </Box>

        }

      />



      {saveError && (

        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>

          {saveError}

        </Alert>

      )}



      {readOnly && (

        <Alert severity="warning" sx={{ mb: 2 }}>

          This vendor is in <strong>{approvalStatus?.replace(/_/g, ' ')}</strong> status and cannot be edited here.

          {approvalStatus === 'PENDING_APPROVAL' && ' Awaiting reviewer action.'}

          {approvalStatus === 'BLACKLISTED' && ' Blacklisted vendors are read-only.'}

        </Alert>

      )}



      {formMode === 'limited' && (

        <Alert severity="info" sx={{ mb: 2 }}>

          Approved vendor — only contact, commercial, banking, and document fields can be updated.

          Code, type, and tax identifiers are locked.

        </Alert>

      )}



      {isEdit && !isVendorActive({ is_active: isActive }) && !readOnly && (

        <Alert severity="warning" sx={{ mb: 2 }}>

          This vendor is <strong>inactive</strong> and cannot be submitted for approval or used in new purchase orders.

          Set status to <strong>Active</strong> and save to continue the approval workflow.

        </Alert>

      )}



      <Box

        component="form"

        id={FORM_ID}

        onSubmit={readOnly ? (e) => e.preventDefault() : handleSubmit(onSubmit, onInvalid)}

        sx={{ ...cardSx, p: { xs: 2, sm: 3 }, opacity: readOnly ? 0.85 : 1 }}

      >

        <Grid container spacing={2}>

          <SectionTitle label="Basic Information" first />



          <Grid item xs={12} sm={6} md={4} id="vendor-field-vendor_code">

            <Controller name="vendor_code" control={control}

              rules={{ required: 'Vendor code is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="Vendor Code *"

                  disabled={isEdit || fieldDisabled('vendor_code')} error={errors.vendor_code} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4} id="vendor-field-vendor_name">

            <Controller name="vendor_name" control={control}

              rules={{ required: 'Vendor name is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="Vendor Name *"

                  disabled={fieldDisabled('vendor_name')} error={errors.vendor_name} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4}>

            <Controller name="legal_name" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Legal / Registered Name" disabled={fieldDisabled('legal_name')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4} id="vendor-field-vendor_type">

            <Controller name="vendor_type" control={control}

              rules={{ required: 'Vendor type is required' }}

              render={({ field }) => (

                <InvSelect label="Vendor Type *" value={field.value} onChange={field.onChange}
                  inputRef={field.ref} name={field.name}
                  options={typeOptions} error={errors.vendor_type} disabled={fieldDisabled('vendor_type')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4}>

            <Controller name="vendor_category" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Vendor Category" disabled={fieldDisabled('vendor_category')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4}>

            <Controller name="rating" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Rating (0–5)" type="number"

                  inputProps={{ min: 0, max: 5, step: 0.1 }} disabled={fieldDisabled('rating')} />

              )} />

          </Grid>



          {isEdit && (

            <Grid item xs={12} sm={6} md={4}>

              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mb: 0.5, fontWeight: 500 }}>

                Status *

              </Typography>

              <Controller name="is_active" control={control}

                render={({ field }) => (

                  <RadioGroup row value={field.value ? 'active' : 'inactive'}

                    onChange={(e) => field.onChange(e.target.value === 'active')}>

                    <FormControlLabel value="active" control={<Radio size="small" />} label="Active"

                      disabled={fieldDisabled('is_active')}

                      sx={{ color: IPD_COLORS.textPrimary }} />

                    <FormControlLabel value="inactive" control={<Radio size="small" />} label="Inactive"

                      disabled={fieldDisabled('is_active')}

                      sx={{ color: IPD_COLORS.textPrimary }} />

                  </RadioGroup>

                )} />

            </Grid>

          )}



          <Grid item xs={12}>

            <Controller name="remarks" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Remarks" multiline rows={2} disabled={fieldDisabled('remarks')} />

              )} />

          </Grid>



          <SectionTitle label="Address" />



          <Grid item xs={12} id="vendor-field-address">

            <Controller name="address" control={control}

              rules={{ required: 'Address is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="Address Line 1 *"

                  disabled={fieldDisabled('address')} error={errors.address} />

              )} />

          </Grid>

          <Grid item xs={12}>

            <Controller name="address_line2" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Address Line 2" disabled={fieldDisabled('address_line2')} />

              )} />

          </Grid>



          <Grid item xs={12} sm={6} md={3} id="vendor-field-state">

            <Controller name="state" control={control}

              rules={{ required: 'State is required' }}

              render={({ field }) => (

                <InvSelect label="State *" value={field.value}
                  inputRef={field.ref} name={field.name}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('city', '');
                    fetchCities(e.target.value);
                  }}
                  options={statesList.map(s => ({ value: s.state_name, label: s.state_name }))}
                  error={errors.state} disabled={fieldDisabled('state')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-city">

            <Controller name="city" control={control}

              rules={{ required: 'City is required' }}

              render={({ field }) => (

                <InvSelect label="City *" value={field.value} onChange={field.onChange}
                  inputRef={field.ref} name={field.name}
                  options={citiesList.map(d => ({ value: d.district_name, label: d.district_name }))}
                  error={errors.city} disabled={fieldDisabled('city') || !watch('state')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-country">

            <Controller name="country" control={control}

              rules={{ required: 'Country is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="Country *" disabled={fieldDisabled('country')} error={errors.country} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-pincode">

            <Controller name="pincode" control={control}

              rules={{ required: 'PIN / ZIP is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="PIN / ZIP Code *"

                  disabled={fieldDisabled('pincode')} error={errors.pincode} />

              )} />

          </Grid>



          <SectionTitle label="Contact" />



          <Grid item xs={12} sm={6} md={4} id="vendor-field-contact_person">

            <Controller name="contact_person" control={control}

              rules={{ required: 'Contact person is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="Contact Person *"

                  disabled={fieldDisabled('contact_person')} error={errors.contact_person} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4} id="vendor-field-phone">

            <Controller name="phone" control={control}

              rules={{
                required: 'Mobile number is required.',
                validate: (value) => {
                  const raw = value ?? '';
                  const trimmed = String(raw).trim();

                  if (!trimmed) return 'Mobile number is required.';
                  if (/\s/.test(trimmed)) return 'Prevent formatting issues.';
                  if (!/^\d+$/.test(trimmed)) return 'No alphabets or symbols.';
                  if (trimmed.length !== 10) return 'Indian mobile numbers are 10 digits.';
                  if (!/^[6-9]/.test(trimmed)) return 'Valid Indian mobile numbers start with these digits.';
                  return true;
                },
              }}

              render={({ field }) => (

                <InvTextField {...field} label="Mobile Number *"

                  disabled={fieldDisabled('phone')}
                  error={errors.phone}
                  type="tel"
                  inputProps={{ inputMode: 'numeric', maxLength: 10 }}
                  onChange={(e) => field.onChange(e.target.value.trim())} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4}>

            <Controller name="alternate_phone" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Alternate Phone" disabled={fieldDisabled('alternate_phone')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={4} id="vendor-field-email">

            <Controller name="email" control={control}

              rules={{

                required: 'Email is required',

                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },

              }}

              render={({ field }) => (

                <InvTextField {...field} label="Email ID *" type="email"

                  disabled={fieldDisabled('email')} error={errors.email} />

              )} />

          </Grid>



          <SectionTitle label="Tax & Compliance" />



          <Grid item xs={12} sm={6} md={3} id="vendor-field-gstin">

            <Controller name="gstin" control={control}

              rules={{ required: 'GST number is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="GST Number *"

                  disabled={fieldDisabled('gstin')} error={errors.gstin} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-pan">

            <Controller name="pan" control={control}

              rules={{ required: 'PAN number is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="PAN Number *"

                  disabled={fieldDisabled('pan')} error={errors.pan} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-drug_license_no">

            <Controller name="drug_license_no" control={control}

              rules={{
                required: isPharma ? 'Drug License No. is required' : false,
              }}

              render={({ field }) => (

                <InvTextField {...field} label={isPharma ? 'Drug License No. *' : 'Drug License No.'}

                  disabled={fieldDisabled('drug_license_no')} error={errors.drug_license_no} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-license_valid_till">

            <Controller name="license_valid_till" control={control}

              rules={{
                required: isPharma ? 'License validity date is required' : false,
              }}

              render={({ field }) => (

                <InvTextField {...field} label={isPharma ? 'License Valid Till *' : 'License Valid Till'} type="date"

                  disabled={fieldDisabled('license_valid_till')}

                  error={errors.license_valid_till}

                  InputLabelProps={{ shrink: true }} />

              )} />

          </Grid>



          <SectionTitle label="Commercial & Banking" />



          <Grid item xs={12} sm={6} md={3} id="vendor-field-payment_terms">

            <Controller name="payment_terms" control={control}

              rules={{ required: 'Payment terms are required' }}

              render={({ field }) => (

                <InvTextField {...field} label="Payment Terms *" placeholder="e.g. Net 30"

                  disabled={fieldDisabled('payment_terms')} error={errors.payment_terms} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-credit_period">

            <Controller name="credit_period" control={control}

              rules={{ required: 'Credit period is required' }}

              render={({ field }) => (

                <InvTextField {...field} label="Credit Period (Days) *" type="number"

                  inputProps={{ min: 0 }} disabled={fieldDisabled('credit_period')} error={errors.credit_period} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3} id="vendor-field-currency">

            <Controller name="currency" control={control}

              rules={{ required: 'Currency is required' }}

              render={({ field }) => (

                <InvSelect label="Currency *" value={field.value} onChange={field.onChange}
                  inputRef={field.ref} name={field.name}
                  options={currencyOptions} error={errors.currency} disabled={fieldDisabled('currency')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3}>

            <Controller name="bank_name" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Bank Name" disabled={fieldDisabled('bank_name')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3}>

            <Controller name="bank_account" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="Account Number" disabled={fieldDisabled('bank_account')} />

              )} />

          </Grid>

          <Grid item xs={12} sm={6} md={3}>

            <Controller name="bank_ifsc" control={control}

              render={({ field }) => (

                <InvTextField {...field} label="IFSC Code" disabled={fieldDisabled('bank_ifsc')} />

              )} />

          </Grid>



          <SectionTitle label="Supporting Documents" />



          <Grid item xs={12}>

            <VendorDocumentsPanel

              control={control}

              setValue={setValue}

              watch={watch}

              vendorType={vendorType}

              disabled={readOnly || !isFieldEditable('documentRows', formMode)}

            />

          </Grid>

        </Grid>

      </Box>

    </Box>

  );

}


